// ---------------------------------------------------------------------------
// useFileDropZone — torna um elemento "droppable" para arquivos (Inputs V3).
// ---------------------------------------------------------------------------
// Reaproveitado por dois consumidores:
//   - ``InputBoxV3`` (cada card da UI principal vira alvo de drop);
//   - ``InputDrawerV3`` (corpo do drawer, exceto o header).
//
// Resolve o classico "flicker" de ``dragenter``/``dragleave`` (que
// disparam tambem ao cruzar fronteiras de elementos-filho) com um
// contador de profundidade: ``isOver`` so volta a false quando o
// cursor realmente sai do elemento raiz.
//
// Importante: o overlay visual de "solte aqui" deve usar
// ``pointer-events: none`` para nao gerar seus proprios eventos de
// enter/leave (senao o contador oscila e o overlay pisca).
// ---------------------------------------------------------------------------

import { useCallback, useRef, useState } from "react";

import { dragHasFiles, extractDroppedFile } from "./dragDropFile";

export default function useFileDropZone({ onFile, onReject, disabled }) {
  const [isOver, setIsOver] = useState(false);
  const depthRef = useRef(0);

  const reset = useCallback(() => {
    depthRef.current = 0;
    setIsOver(false);
  }, []);

  const onDragEnter = useCallback(
    (e) => {
      if (disabled || !dragHasFiles(e)) return;
      e.preventDefault();
      depthRef.current += 1;
      if (!isOver) setIsOver(true);
    },
    [disabled, isOver],
  );

  const onDragOver = useCallback(
    (e) => {
      if (disabled || !dragHasFiles(e)) return;
      // ``preventDefault`` no dragover e' o que habilita o ``drop``.
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
    },
    [disabled],
  );

  const onDragLeave = useCallback(
    (e) => {
      if (disabled || !dragHasFiles(e)) return;
      depthRef.current -= 1;
      if (depthRef.current <= 0) reset();
    },
    [disabled, reset],
  );

  const onDrop = useCallback(
    (e) => {
      if (disabled) return;
      if (!dragHasFiles(e)) {
        reset();
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      reset();
      const { file, error } = extractDroppedFile(e.dataTransfer);
      if (error || !file) {
        if (typeof onReject === "function") onReject(error || "empty");
        return;
      }
      if (typeof onFile === "function") onFile(file);
    },
    [disabled, onFile, onReject, reset],
  );

  return {
    isOver: isOver && !disabled,
    dropHandlers: { onDragEnter, onDragOver, onDragLeave, onDrop },
  };
}
